import {useMutation, UseMutationResult} from '@tanstack/react-query';

import {Locale} from '../../../../provider/locale/locale';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {postBulkReviewTags} from '../../../../service/reviews/reviews';
import {mergeBulkTagsIntoReview} from '../../../../service/reviews/reviews-bulk-tags-merge';
import {TagDataType} from '../../../../service/reviews/reviews-tags';
import {BulkReviewTagsResultType, ReviewDataType} from '../../../../service/reviews/reviews-type';

import {ReviewDataWithCatalogType} from './review-list/review-list-type';

type BulkReviewTagsApplyMutationVariablesType = {
    listPageReviewsSnapshot: Array<ReviewDataType>;
    reviewIds: Array<number>;
    selectedReviewsSnapshot: Array<ReviewDataWithCatalogType>;
    selectedTags: Array<TagDataType>;
    tagIds: Array<number>;
};

type UseBulkReviewTagsApplyMutationOptionsType = {
    onFlowComplete?: () => void;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
};

export function useBulkReviewTagsApplyMutation(
    options: UseBulkReviewTagsApplyMutationOptionsType
): UseMutationResult<BulkReviewTagsResultType, unknown, BulkReviewTagsApplyMutationVariablesType> {
    const {onFlowComplete, updateReview} = options;
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: ({reviewIds, tagIds}: BulkReviewTagsApplyMutationVariablesType) =>
            postBulkReviewTags({reviewIds, tagIds}),
        onError: () => {
            snackbar.error(<Locale stringKey="REVIEWS__BULK_TAGS__ERROR" />);
        },
        onSuccess: (result, variables) => {
            const {listPageReviewsSnapshot, selectedReviewsSnapshot, selectedTags} = variables;

            result.processedReviewIds.forEach((reviewId) => {
                const fromSelection = selectedReviewsSnapshot.find((review) => review.id === reviewId);
                const fromList = listPageReviewsSnapshot.find((review) => review.id === reviewId);
                const mergedTags = mergeBulkTagsIntoReview(fromSelection?.tags ?? fromList?.tags, selectedTags);

                updateReview(reviewId, {tags: mergedTags});
            });

            const skippedCount = result.skippedReviewIds.length;
            const processedCount = result.processedReviewIds.length;

            if (skippedCount > 0) {
                snackbar.success({
                    message: <Locale stringKey="REVIEWS__BULK_TAGS__SUCCESS_TITLE" />,
                    description: (
                        <Locale
                            stringKey="REVIEWS__BULK_TAGS__PARTIAL_DESCRIPTION"
                            valueMap={{
                                processedCount: String(processedCount),
                                skippedCount: String(skippedCount),
                            }}
                        />
                    ),
                });
            } else {
                snackbar.success({
                    message: <Locale stringKey="REVIEWS__BULK_TAGS__SUCCESS_TITLE" />,
                    description: (
                        <Locale
                            stringKey="REVIEWS__BULK_TAGS__SUCCESS_DESCRIPTION"
                            valueMap={{processedCount: String(processedCount)}}
                        />
                    ),
                });
            }

            onFlowComplete?.();
        },
    });
}
